import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar, Leaf, Share } from "@phosphor-icons/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { fetchPublicInsight } from "@/lib/api";
import { toast } from "sonner";

export default function InsightPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setPost(await fetchPublicInsight(slug));
      } catch {
        setPost(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post?.title, text: post?.teaser, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link kopiert");
      }
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-8 text-sm text-zinc-400" data-testid="post-loading">
        Lade…
      </div>
    );
  }
  if (!post) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-8" data-testid="post-missing">
        <Link to="/insights" className="text-sm text-zinc-400 hover:text-white flex items-center gap-2">
          <ArrowLeft size={14} /> Zurück zur Übersicht
        </Link>
        <h1 className="text-2xl font-medium mt-4">Post nicht gefunden</h1>
      </div>
    );
  }

  const regions = post.regions || [];

  const chartData = regions.map((r, i) => ({
    name: r.region_label,
    yield: r.yield_forecast_index,
    ndvi: (r.ndvi_current ?? 0) * 100,
    forest: r.forest_vitality_index,
  }));

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative z-10" data-testid="insight-post-root">
      <Helmet>
        <title>{post.title} · EID Weekly Insights</title>
        <meta name="description" content={post.teaser} />
        <link rel="canonical" href={`https://bodenblick.com/insights/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.teaser} />
        <meta property="og:image" content="https://images.unsplash.com/photo-1621452353638-888c49e1d340?w=1200" />
        <meta property="article:published_time" content={post.created_at} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.teaser,
          "datePublished": post.created_at,
          "author": { "@type": "Organization", "name": "Ecological Intelligence Dashboard" },
          "publisher": { "@type": "Organization", "name": "EID" },
        })}</script>
      </Helmet>
      <header className="border-b border-white/10 bg-[#121212]">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/insights" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft size={16} /> Alle Ausgaben
          </Link>
          <button
            onClick={onShare}
            data-testid="share-btn"
            className="flex items-center gap-2 text-sm px-3 py-1.5 border border-white/10 rounded-sm hover:bg-white/5"
          >
            <Share size={14} /> Teilen
          </button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 text-xs font-mono text-zinc-500">
          <Calendar size={12} />
          <span>{(post.created_at || "").slice(0, 10)}</span>
          <span>·</span>
          <span className="uppercase">{post.slug}</span>
        </div>
        <h1 className="text-3xl md:text-5xl font-medium tracking-tight mt-3">{post.title}</h1>
        <p className="text-lg text-zinc-400 mt-3">{post.teaser}</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {regions.map((r, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-[#00FA9A]/10 text-[#00FA9A] border border-[#00FA9A]/40 px-2 py-0.5 rounded-sm"
            >
              <Leaf size={10} /> {r.region_label} · {r.crop}
            </span>
          ))}
        </div>

        <div className="mt-8 eid-card p-4" data-testid="insight-chart">
          <h3 className="text-sm font-medium uppercase tracking-wider text-zinc-400 mb-2">
            Kennzahlen dieser Woche
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 16, right: 12, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
              <XAxis dataKey="name" fontSize={11} stroke="#8A8A93" />
              <YAxis fontSize={10} stroke="#8A8A93" />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", color: "white", fontFamily: "JetBrains Mono", fontSize: 11 }} />
              <Line type="monotone" dataKey="yield" name="Ertragsprognose /100" stroke="#FFC24B" strokeWidth={2} dot />
              <Line type="monotone" dataKey="forest" name="Forst /100" stroke="#00FA9A" strokeWidth={2} dot />
              <Line type="monotone" dataKey="ndvi" name="NDVI ×100" stroke="#4DA3FF" strokeWidth={2} dot />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div
          className="prose prose-sm max-w-[72ch] mt-8 text-white leading-relaxed whitespace-pre-wrap"
          data-testid="insight-body"
          style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
        >
          {post.body}
        </div>

        <div className="eid-divider my-10" />

        <p className="text-xs text-zinc-500 font-mono">
          Dieser Beitrag wurde automatisch aus anonymisierten Satellit-, Wetter- und Futures-Daten generiert
          (Copernicus Sentinel, Open-Meteo, yfinance) und redaktionell via Gemini 3 Flash formuliert.
          Keine exakten Standorte werden offengelegt.
        </p>
      </article>
    </div>
  );
}
